import {
  AM_GROUP_MANAGEMENT,
  AUTH_CONFIG_AFFILIATION_TYPE,
  EXAM_LAYOUT_PATTERN,
  HUB_CONFIGURATION,
  OBJECT_EXPORT,
  OBJECT_IMPORT,
  OUTLOOK_AUDIT_LOGS,
  OUTLOOK_INTEGRATION,
  REPORTING_HEATMAP_PAGE,
  USE_AOP_TOPBAR,
} from 'constants/featureFlag.constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { userSelector } from 'slices/auth.slice';
import { organizationSelector } from 'slices/organization.slice';
import { useAppSelector } from 'slices/store';
import type { ITEToken, TOrganization } from '@timeedit/types';

export type FlagSet = {
  [OUTLOOK_INTEGRATION]: boolean;
  [OUTLOOK_AUDIT_LOGS]: boolean;
  [AM_GROUP_MANAGEMENT]: boolean;
  [OBJECT_EXPORT]: boolean;
  [OBJECT_IMPORT]: boolean;
  [EXAM_LAYOUT_PATTERN]: boolean;
  [REPORTING_HEATMAP_PAGE]: boolean;
  [AUTH_CONFIG_AFFILIATION_TYPE]: 'embedded' | 'both' | 'separated';
  [USE_AOP_TOPBAR]: 'off' | 'rootOptIn' | 'on';
  [HUB_CONFIGURATION]: boolean;
};

export type FlagKey = keyof FlagSet;

/**
 * We currently use two types of feature flags:
 * 1. Toggle flags: These are boolean flags that are either true or false.
 * 2. Organization flags: These are flags that are an array of organization IDs.
 */
export const useLaunchDarklyFlag = <T extends FlagKey>(
  flagKey: T
): FlagSet[T] => {
  const flags = useFlags<FlagSet>();
  return flags[flagKey];
};

const ALL_ORGS = 'all';

export function useFeatureFlag(
  flagKey: FlagKey,
  checkCustomerSignature = false
) {
  const flag = useLaunchDarklyFlag(flagKey);
  const user: ITEToken = useAppSelector(userSelector);
  const organization: TOrganization = useAppSelector(organizationSelector);

  return isFeatureEnabled(flag, user, organization, checkCustomerSignature);
}

/**
 * Same as `useFeatureFlag`, for use outside React, e.g. in route checks.
 * Flags are either booleans or lists of organization ids (or `'all'`).
 */
export function isFeatureEnabled(
  flagValue: unknown,
  user: ITEToken | undefined,
  organization: TOrganization | undefined,
  checkCustomerSignature = false
) {
  const flag = (flagValue ?? []) as boolean | string[];

  if (typeof flag === 'boolean') {
    return flag;
  }

  if (flag.length === 1 && flag[0] === ALL_ORGS) {
    return true;
  }

  if (!checkCustomerSignature) {
    return flag.includes(user?.organizationId);
  }

  return (
    flag.includes(user?.organizationId) ||
    flag.includes(organization?.customerSignature)
  );
}

export const useEnableOutlookIntegration = () =>
  useFeatureFlag(OUTLOOK_INTEGRATION);

export const useEnableOutlookAuditLogs = () =>
  useFeatureFlag(OUTLOOK_AUDIT_LOGS);

export const useEnableGroupManagement = () =>
  useFeatureFlag(AM_GROUP_MANAGEMENT);

export const useObjectExportEnabled = () => useFeatureFlag(OBJECT_EXPORT);

export const useObjectImportEnabled = () => useFeatureFlag(OBJECT_IMPORT);

export const useExamLayoutPattern = () => useFeatureFlag(EXAM_LAYOUT_PATTERN);

export const useReportingHeatmapPage = () =>
  useFeatureFlag(REPORTING_HEATMAP_PAGE);

export const useHubConfigurationEnabled = () =>
  useFeatureFlag(HUB_CONFIGURATION);

export const useAuthConfigAffiliationType = () =>
  useLaunchDarklyFlag(AUTH_CONFIG_AFFILIATION_TYPE);

export const useAopTopBarFlag = () => useLaunchDarklyFlag(USE_AOP_TOPBAR);
